import { useCallback } from 'react';

import { ChatMessage, ChatRequestSchema, ChatStreamChunk, MemoryRegistry, ToolsRegistry } from '@repo/contracts';

export interface UseChatAPIProps {
    chatUrl: string;
}

export function useChatAPI({ chatUrl }: UseChatAPIProps) {

    /**
     * Envoie une requête de chat en streaming.
     * @param messages L'historique des messages
     * @param MemoryRegistry Le contexte applicatif
     * @param ToolsRegistry Les outils disponibles
     * @param onChunk Callback appelé à chaque morceau de texte reçu (pour l'affichage en temps réel)
     * @param signal Signal d'annulation
     * @returns Une promesse qui se résout quand le flux complet est terminé.
     */
    const sendStreamChatRequest = useCallback(
        async (
            conversationId: string,
            messages: ChatMessage[],
            registry: MemoryRegistry,
            tools: ToolsRegistry,
            onChunk: (chunk: ChatStreamChunk) => void,
            signal?: AbortSignal,
        ) => {
            const payload = { 
                conversationId, 
                messages, 
                requestContext: { origin: 'web' },
                appContext: registry, 
                tools, 
            };
            const parseResult = ChatRequestSchema.safeParse(payload);
            if (!parseResult.success) {
                console.error('Zod parse error on chat request:', parseResult.error);
                throw new Error('Invalid payload');
            }

            try {
                const response = await fetch(`${chatUrl}/api/chat/stream`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify(parseResult.data),
                    signal,
                });

                if (response.status === 429) {
                    throw new Error('QuotaExceeded');
                }
                if (!response.ok) throw new Error(`Erreur API: ${response.status}: ${await response.text()}`);
                if (!response.body) throw new Error('No response body');

                const decoder = new TextDecoder();
                let buffer = '';

                for await (const chunk of response.body as unknown as AsyncIterable<Uint8Array>) {
                    buffer += decoder.decode(chunk, { stream: true });
                    const lines = buffer.split('\n');
                    buffer = lines.pop() || '';

                    for (const line of lines) {
                        if (line.startsWith('data: ')) {
                            const data = line.slice(6);
                            if (data === '[DONE]') return;
                            try {
                                const parsedChunk = JSON.parse(data);
                                onChunk(parsedChunk);
                            } catch {
                                throw new Error('Failed to parse chunk');
                            }
                        }
                    }
                }
            } catch (error: unknown) {
                if (error instanceof Error && error.name === 'AbortError') {
                    console.log('Génération arrêtée par l\'utilisateur.');
                    return; 
                }
                throw error;
            }
        },
        [chatUrl],
    );

    const fetchQuota = useCallback(async (): Promise<{ max: number; remaining: number | null }> => {
        const response = await fetch(`${chatUrl}/api/chat/quota`, {
            method: 'GET',
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch quota: ${response.status}`);
        }
        return await response.json();
    }, [chatUrl]);

    const fetchPublicSettings = useCallback(async (): Promise<{ conversationRetentionDays: number }> => {
        const response = await fetch(`${chatUrl}/api/chat/public-settings`, {
            method: 'GET',
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch public settings: ${response.status}`);
        }
        return await response.json();
    }, [chatUrl]);

    const generateTitle = useCallback(async (message: string, assistantResponse?: string): Promise<string> => {
        const response = await fetch(`${chatUrl}/api/chat/title`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ message, assistantResponse }),
        });
        if (!response.ok) {
            throw new Error(`Failed to generate title: ${response.status}`);
        }
        const data = await response.json();
        return data.title;
    }, [chatUrl]);

    const fetchConversations = useCallback(async (): Promise<any[]> => {
        const response = await fetch(`${chatUrl}/api/chat/conversations`, {
            method: 'GET',
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error(`Failed to fetch conversations: ${response.status}`);
        }
        return await response.json();
    }, [chatUrl]);

    const saveConversation = useCallback(async (conversation: any): Promise<void> => {
        const response = await fetch(`${chatUrl}/api/chat/conversations`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(conversation),
        });
        if (!response.ok) {
            let errorText = '';
            try { errorText = await response.text(); } catch { /* empty */ }
            console.error('Save conversation error:', errorText);
            throw new Error(`Failed to save conversation: ${response.status} - ${errorText}`);
        }
    }, [chatUrl]);

    const updateConversation = useCallback(async (id: string, data: any): Promise<void> => {
        const response = await fetch(`${chatUrl}/api/chat/conversations/${id}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify(data),
        });
        if (!response.ok) {
            throw new Error(`Failed to update conversation: ${response.status}`);
        }
    }, [chatUrl]);

    const deleteConversationApi = useCallback(async (id: string): Promise<void> => {
        const response = await fetch(`${chatUrl}/api/chat/conversations/${id}/delete`, {
            method: 'POST',
            credentials: 'include',
        });
        if (!response.ok) {
            throw new Error(`Failed to delete conversation: ${response.status}`);
        }
    }, [chatUrl]);

    return { sendStreamChatRequest, fetchQuota, fetchPublicSettings, generateTitle, fetchConversations, saveConversation, updateConversation, deleteConversationApi };
}